import type { V2AdminCreditDetailDto } from '@idc-forms/api-client'

type CreditStageDto = V2AdminCreditDetailDto['stages'][number]
type CreditFieldValueDto = NonNullable<CreditStageDto['items'][number]['fields'][number]['value']>
type CreditAttachmentDto = Extract<CreditFieldValueDto, { kind: 'files' }>['attachments'][number]

export type CreditDocumentView = CreditAttachmentDto & {
  stageName: string
  itemLabel: string
  fieldLabel: string
}

export type CreditStageTimelineItem = Pick<
  CreditStageDto,
  'key' | 'order' | 'name' | 'status' | 'enteredAt' | 'exitedAt' | 'completedBy'
> & {
  durationDays: number | null
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

function durationInDays(start: string | null, end: string | null, now: Date): number | null {
  if (!start) return null
  const startTime = new Date(start).getTime()
  const endTime = end ? new Date(end).getTime() : now.getTime()
  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return null
  return Math.max(0, Math.floor((endTime - startTime) / MS_PER_DAY))
}

export function buildCreditStageTimeline(
  stages: CreditStageDto[],
  now = new Date()
): CreditStageTimelineItem[] {
  return stages
    .map((stage) => ({
      key: stage.key,
      order: stage.order,
      name: stage.name,
      status: stage.status,
      enteredAt: stage.enteredAt,
      exitedAt: stage.exitedAt,
      completedBy: stage.completedBy,
      durationDays: durationInDays(stage.enteredAt, stage.exitedAt, now)
    }))
    .sort((left, right) => left.order - right.order)
}

export function extractCreditDocuments(stages: CreditStageDto[]): CreditDocumentView[] {
  return stages.flatMap((stage) =>
    stage.items.flatMap((item) =>
      item.fields.flatMap((field) =>
        field.value?.kind === 'files'
          ? field.value.attachments.map((attachment) => ({
              ...attachment,
              stageName: stage.name,
              itemLabel: item.label,
              fieldLabel: field.label
            }))
          : []
      )
    )
  )
}

export function describeCreditPosition(
  detail: Pick<V2AdminCreditDetailDto, 'status' | 'currentStageKey' | 'stages'>
): string {
  if (detail.status === 'approved') {
    return 'El crédito fue aprobado y la negociación está lista para entrega.'
  }
  if (detail.status === 'cancelled') {
    const stage = detail.stages.find((item) => item.key === detail.currentStageKey)
    return stage
      ? `El trámite fue cancelado durante ${stage.name}.`
      : 'El trámite de crédito fue cancelado.'
  }

  const stage = detail.stages.find((item) => item.key === detail.currentStageKey)
  if (!stage) return 'El trámite está en proceso y aún no tiene una etapa activa.'
  return `El trámite está en ${stage.name}.`
}
